import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Plus } from 'lucide-react';
import React from 'react';
import { useDrop } from 'react-dnd';
import type { PlotCard } from '../types';
import { DraggablePlotCard } from './DraggablePlotCard';

// Define the item type for drag and drop
const ITEM_TYPE = 'PLOT_CARD';

interface DroppableCellProps {
    plotLineId: number;
    chapterId: number;
    cards: PlotCard[];
    selectedItems: PlotCard[];
    compactMode: boolean;
    onCardSelect: (card: PlotCard, isMultiSelect?: boolean) => void;
    onAddCard: (plotLineId: number, chapterId: number) => void;
    onMoveCard: (cardId: number, newPlotLineId: number, newChapterId: number, newPosition?: number) => void;
    onEditCard?: (card: PlotCard) => void;
    onDeleteCard?: (card: PlotCard) => void;
    onCopyCard?: (card: PlotCard) => void;
    onShowDetails?: (card: PlotCard) => void;
    isLastRow: boolean;
    onCardContextMenu?: (card: PlotCard, e: React.MouseEvent) => void;
}

export const DroppableCell: React.FC<DroppableCellProps> = ({
    plotLineId,
    chapterId,
    cards,
    selectedItems,
    compactMode,
    onCardSelect,
    onAddCard,
    onMoveCard,
    onEditCard,
    onDeleteCard,
    onCopyCard,
    onShowDetails,
    isLastRow,
    onCardContextMenu
}) => {


    // We don't need to explicitly call useMultiplePlotcardTags here anymore
    // The individual DraggablePlotCard components will use the cached data
    // from the prefetch in Timeline.tsx
    // Set up drop
    const [{ isOver, canDrop }, drop] = useDrop({
        accept: ITEM_TYPE,
        drop: (item: { id: number, x: number, y: number, position?: number }) => {
            console.log('Dropping card:', item, 'into chapter:', chapterId, 'plotLine:', plotLineId);

            // If the card is from the same chapter, we're reordering within the chapter
            if (item.y === chapterId && item.x === plotLineId) {
                // Find the highest position in the chapter
                const maxPosition = cards.reduce((max, card) => {
                    const cardPos = card.position !== null && card.position !== undefined ? card.position : 0;
                    return cardPos > max ? cardPos : max;
                }, -1);

                console.log('Reordering card within chapter. Max position:', maxPosition);

                // Set the new position to be at the end of the chapter
                onMoveCard(item.id, plotLineId, chapterId, maxPosition + 1);
            } else {
                // Moving from a different chapter or plot line
                console.log('Moving card from different chapter/plotLine to chapter:', chapterId);

                // When moving to a new chapter, we want to place it at the end
                const maxPosition = cards.reduce((max, card) => {
                    const cardPos = card.position !== null && card.position !== undefined ? card.position : 0;
                    return cardPos > max ? cardPos : max;
                }, -1);

                // Move the card to the new chapter and set its position
                onMoveCard(item.id, plotLineId, chapterId, maxPosition + 1);
            }
        },
        collect: (monitor) => ({
            isOver: !!monitor.isOver(),
            canDrop: !!monitor.canDrop()
        })
    });

    const handleCardContextMenu = (card: PlotCard, e: React.MouseEvent) => {
        if (onCardContextMenu) {
            onCardContextMenu(card, e);
        }
    };

    return (
        <div
            ref={drop}
            className={cn(
                "relative px-4 py-3 border-b border-l border-border min-h-[140px] group transition-colors duration-200",
                isLastRow ? "border-b-0" : "",
                "hover:bg-muted/20",
                isOver && canDrop && "bg-primary/5 ring-1 ring-primary/20",
                isOver && !canDrop && "bg-destructive/5 ring-1 ring-destructive/20"
            )}
        >
            {/* Add card button */}
            <Button
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 p-0"
                onClick={() => onAddCard(plotLineId, chapterId)}
            >
                <Plus className="h-3.5 w-3.5" />
            </Button>

            {/* Cards */}
            <div className="space-y-2.5 pt-6">
                {cards
                    .sort((a, b) => {
                        // Sort by position if available
                        const posA = a.position !== null && a.position !== undefined ? a.position : 0;
                        const posB = b.position !== null && b.position !== undefined ? b.position : 0;
                        return posA - posB;
                    })
                    .map((card, index) => (
                        <div
                            key={card.id}
                            onContextMenu={(e) => handleCardContextMenu(card, e)}
                            className="animate-in fade-in-50 duration-200"
                        >
                            <DraggablePlotCard
                                card={card}
                                index={index}
                                isSelected={selectedItems.some(item => item.id === card.id)}
                                compactMode={compactMode}
                                onSelect={onCardSelect}
                                onMoveCard={onMoveCard}
                                onEdit={onEditCard}
                                onDelete={onDeleteCard}
                                onCopy={onCopyCard}
                                onShowDetails={onShowDetails}
                            />
                        </div>
                    ))
                }
            </div>
        </div>
    );
};